<script lang="ts">
  // Race / area search: "112", "Mount Pleasant", "sales tax". Arrow keys + Enter; Esc closes.
  import type { AreaDef, Overview } from '../../types';
  import { app } from '../../lib/app.svelte';
  import { GROUP_LABEL, scopeLabel } from '../../lib/race';
  let { ov }: { ov: Overview | null } = $props();
  let q = $state('');
  let open = $state(false);
  let active = $state(0);
  let input: HTMLInputElement | undefined = $state();

  type Hit = { kind: 'race'; id: string; title: string; sub: string } | { kind: 'area'; area: AreaDef };
  const hits = $derived.by((): Hit[] => {
    const s = q.trim().toLowerCase();
    if (!s || !ov) return [];
    const toks = s.split(/\s+/);
    const match = (hay: string) => toks.every((t) => hay.includes(t));
    const races: Hit[] = ov.races
      .filter((r) => match(`${r.title} ${r.subtitle ?? ''} ${GROUP_LABEL[r.group]} ${r.race_id} ${r.scope === 'statewide' ? 'statewide sc' : 'charleston'}`.toLowerCase()))
      .slice(0, 7)
      .map((r) => ({ kind: 'race', id: r.race_id, title: r.title, sub: [scopeLabel(r, true), r.subtitle && !/county|statewide/i.test(r.subtitle) ? r.subtitle : null].filter(Boolean).join(' · ') }));
    const areas: Hit[] = ov.areas.filter((a) => a.kind !== 'county' && match(a.label.toLowerCase())).slice(0, 3).map((a) => ({ kind: 'area', area: a }));
    return [...areas, ...races];
  });

  function choose(h: Hit) {
    if (h.kind === 'race') location.hash = app.href(`#/race/${encodeURIComponent(h.id)}`);
    else { app.setArea(h.area.area_id); location.hash = app.href(`#/?area=${encodeURIComponent(h.area.area_id)}`); }
    q = ''; open = false; input?.blur();
  }
  function onkey(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') { active = Math.min(hits.length - 1, active + 1); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { active = Math.max(0, active - 1); e.preventDefault(); }
    else if (e.key === 'Enter' && hits[active]) { choose(hits[active]!); e.preventDefault(); }
    else if (e.key === 'Escape') { open = false; input?.blur(); }
  }
</script>

<div class="search" class:open>
  <svg class="ico" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2" /><path d="M20 20l-3.5-3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
  <input bind:this={input} bind:value={q} type="search" placeholder="Find a race or place…" aria-label="Find a race or place"
    role="combobox" aria-expanded={open && hits.length > 0} aria-controls="search-list" aria-autocomplete="list"
    aria-activedescendant={open && hits[active] ? `search-opt-${active}` : undefined}
    onfocus={() => (open = true)} onblur={() => setTimeout(() => (open = false), 150)} oninput={() => { open = true; active = 0; }} onkeydown={onkey} />
  {#if open && hits.length}
    <ul id="search-list" role="listbox">
      {#each hits as h, i}
        <li role="option" id="search-opt-{i}" aria-selected={i === active}>
          <button type="button" onmousedown={(e) => e.preventDefault()} onclick={() => choose(h)} class:act={i === active}>
            {#if h.kind === 'race'}<span class="t">{h.title}</span><span class="s">{h.sub}</span>
            {:else}<span class="t">Show races for {h.area.label}</span><span class="s">Area</span>{/if}
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  /* a fixed 40px slot in the bar; the field opens leftwards over its neighbours, so nothing reflows */
  .search { position: relative; width: 40px; height: 38px; flex: none; }
  .ico { position: absolute; left: 12px; top: 11px; color: var(--ink-3); pointer-events: none; z-index: 2; }
  /* An icon at every width that opens into a field overlaying the bar (absolutely positioned, so focusing it never
     shifts the tabs / switcher / status): the bar has no room for a permanent field next to the dataset switcher. */
  input { position: absolute; right: 0; top: 0; height: 38px; z-index: 1; width: 40px; border: 1px solid var(--line); background: var(--card-2); border-radius: 999px; padding: 8px 0 8px 34px; font-size: 0.88rem; outline: none; transition: width 0.2s, background 0.2s; color: transparent; cursor: pointer; }
  input::placeholder { color: transparent; }
  input:focus { width: min(78vw, 360px); background: var(--card); border-color: var(--focus); color: var(--ink); padding-right: 12px; cursor: text; }
  input:focus::placeholder { color: var(--ink-3); }
  .search:focus-within { z-index: 70; }
  ul { position: absolute; top: calc(100% + 6px); right: 0; width: min(360px, 92vw); list-style: none; margin: 0; padding: 6px; background: var(--card); border: 1px solid var(--line); border-radius: 14px; box-shadow: var(--shadow-lg); z-index: 60; }
  li button { width: 100%; text-align: left; border: 0; background: none; padding: 9px 10px; border-radius: 9px; display: flex; flex-direction: column; cursor: pointer; }
  li button.act, li button:hover { background: var(--card-3); }
  .t { font-weight: 600; font-size: 0.9rem; }
  .s { font-size: 0.78rem; color: var(--ink-3); }

</style>
